import { useContext } from 'react'
import { LabRemoteConfigKeys } from 'constants/lab'
import GeoContext from 'contexts/geoContext'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import { useAppSelector } from 'hooks/reduxHooks'
import { isAccommodationItem } from 'lib/checkout/checkoutUtils'
import { isRecord } from 'lib/object/objectUtils'
import { readTrimmedString, readTrimmedStringArray } from 'loyalty/lib/labPayloadUtils'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { selectLoggedIn } from 'selectors/accountSelectors'

export interface LuxPlusOfferVoucher {
  voucherAmount: number
}

interface LuxPlusOfferVoucherConfig {
  offerIds: Array<string>
  voucherAmountPerRegion: Record<string, number>
  startTime: number
  endTime?: number
}

function isVoucherAmountPerRegion(value: unknown): value is Record<string, number> {
  return isRecord(value) && Object.values(value).every(amount => typeof amount === 'number' && amount > 0)
}

function getLuxPlusOfferVoucherConfig(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): LuxPlusOfferVoucherConfig | undefined {
  const { value } = remoteConfig ?? {}
  if (!isRecord(value)) return undefined

  const offerIds = readTrimmedStringArray(value.offerIds)
  const voucherAmountPerRegion = value.voucherAmountPerRegion
  const startAt = readTrimmedString(value.startAt)
  const endAt = readTrimmedString(value.endAt)
  if (!offerIds || !isVoucherAmountPerRegion(voucherAmountPerRegion) || !startAt) return undefined

  const startTime = new Date(startAt).getTime()
  const endTime = endAt ? new Date(endAt).getTime() : undefined
  if (Number.isNaN(startTime) || (endTime !== undefined && (Number.isNaN(endTime) || endTime < startTime))) return undefined

  return {
    offerIds,
    voucherAmountPerRegion: Object.fromEntries(
      Object.entries(voucherAmountPerRegion).map(([regionCode, amount]) => [regionCode.toUpperCase(), amount]),
    ),
    startTime,
    endTime,
  }
}

function useLuxPlusOfferVoucher(offerIds: Array<string>): LuxPlusOfferVoucher | undefined {
  const remoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.luxPlusOfferVoucher)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const { currentRegionCode } = useContext(GeoContext)

  if (!isLoggedIn || canRedeemLuxPlusBenefits) return undefined

  const voucherConfig = getLuxPlusOfferVoucherConfig(remoteConfig)
  if (!voucherConfig || !offerIds.some(offerId => voucherConfig.offerIds.includes(offerId))) return undefined

  const voucherAmount = voucherConfig.voucherAmountPerRegion[currentRegionCode.toUpperCase()]
  if (voucherAmount === undefined) return undefined

  const nowTime = Date.now()
  if (nowTime < voucherConfig.startTime) return undefined
  if (voucherConfig.endTime !== undefined && nowTime > voucherConfig.endTime) return undefined

  return { voucherAmount }
}

/**
 * Standalone UI render for LP+ offer voucher, because customer will only get auto-assigned
 * at review & pay step of checkout. SO they won't have voucher at this point of checkout.
 * And the offer voucher is not configured to be shown at subscription upsell step.
 */
export function useCheckoutLuxPlusOfferVoucher(): LuxPlusOfferVoucher | undefined {
  const accommodationOfferIds = useAppSelector(state => state.checkout.cart.items
    .filter(isAccommodationItem)
    .map(item => item.offerId))

  return useLuxPlusOfferVoucher(accommodationOfferIds)
}

export default useLuxPlusOfferVoucher
